type EnsureState = {
	min(value: number | undefined, message?: string): EnsureState
	max(value: number | undefined, message?: string): EnsureState
	required(message?: string): EnsureState
	pattern(regex: RegExp, message?: string): EnsureState
	build(): (value: string) => string | undefined
}

class EnsureStateImpl implements EnsureState
{
	private _min?: [number, string | undefined]

	private _max?: [number, string | undefined]

	private _required: [boolean, string | undefined] = [false, undefined]

	private readonly _patterns: [RegExp, string | undefined][] = []

	min(value: number | undefined, message?: string): EnsureState {
		if (!value || Number.isNaN(value)) return this
		this._min = [value, message]
		return this
	}

	max(value: number | undefined, message?: string | undefined): EnsureState {
		if (!value || Number.isNaN(value)) return this
		this._max = [value, message]
		return this
	}

	required(message?: string | undefined): EnsureState {
		this._required = [true, message]
		return this
	}

	pattern(regex: RegExp, message?: string | undefined): EnsureState {
		this._patterns.push([regex, message])
		return this
	}

	getMinDefaultMessage(): string {
		return `Must be greater than ${this._min?.[0]}`
	}

	getMaxDefaultMessage(): string {
		return `Must be less than ${this._max?.[0]}`
	}

	getPatternDefaultMessage(): string {
		return 'Wrong format'
	}

	getRequiredDefaultMessage(): string {
		return 'Required!'
	}

	validateRequired(value: string): string | undefined {
		if (this._required[0] && value.length === 0) {
			return this._required[1] ?? this.getRequiredDefaultMessage()
		}
	}

	validateMin(value: string): string | undefined {
		const min = this._min
		if (!min) return

		const num = parseFloat(value)
		if (num < min[0]) {
			return min[1] ?? this.getMinDefaultMessage()
		}
	}

	validateMax(value: string): string | undefined {
		const max = this._max
		if (!max) return

		const num = parseFloat(value)
		if (num > max[0]) {
			return max[1] ?? this.getMaxDefaultMessage()
		}
	}

	validatePatterns(value: string): string | undefined {
		const patterns = this._patterns
		if (patterns.length === 0) {
			return
		}

		for (let i = 0; i < patterns.length; i += 1) {
			const pattern = patterns[i][0]
			const message = patterns[i][1]

			if (!pattern.test(value)) {
				return message ?? this.getPatternDefaultMessage()
			}
		}
	}

	build(): (value: string) => string | undefined {
		return (value) => {
			return this.validateRequired(value) || this.validatePatterns(value) || this.validateMin(value) || this.validateMax(value)
		}
	}
}
export const ensure = () => new EnsureStateImpl()
